import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, ShieldCheck } from 'lucide-react';
import { isAxiosError } from 'axios';
import { useAuthStore } from '@/store/auth.store';
import { verifyMfa } from '../services/auth.service';
import { extractServerErrors } from '@/lib/api/error';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

const totpSchema = z.object({
  code: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator app'),
});

const recoverySchema = z.object({
  code: z.string().min(1, 'Recovery code is required'),
});

type CodeValues = z.infer<typeof totpSchema>;

const APP_INITIALS = 'AV';

export function MfaPromptComponent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const challengeToken = searchParams.get('ct') ?? '';
  const setTokens = useAuthStore((s) => s.setTokens);
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);

  const schema = useRecoveryCode ? recoverySchema : totpSchema;

  const form = useForm<CodeValues>({
    resolver: zodResolver(schema),
    defaultValues: { code: '' },
  });
  const { isSubmitting } = form.formState;

  async function onSubmit(values: CodeValues) {
    if (!challengeToken) {
      toast.error('Missing challenge token. Please sign in again.');
      navigate('/login', { replace: true });
      return;
    }
    try {
      const result = await verifyMfa(challengeToken, values.code);
      setTokens(
        result.accessToken,
        result.refreshToken,
        new Date(result.accessTokenExpiresAt),
        null,
      );
      navigate('/', { replace: true });
    } catch (err) {
      if (isAxiosError(err) && err.response) {
        const msgs = extractServerErrors(err);
        form.setError('code', {
          message: msgs[0] ?? 'Invalid code. Please try again.',
        });
      } else {
        toast.error('Network error. Please check your connection.');
      }
    }
  }

  function toggleMode() {
    setUseRecoveryCode((v) => !v);
    form.reset({ code: '' });
  }

  return (
    <div className="flex min-h-screen items-start justify-center bg-muted/30 px-4 pb-4 pt-20 sm:pt-28">
      <Card className="w-full max-w-sm border-border/50 shadow-xl">
        <CardHeader className="space-y-3 pb-4">
          <div className="flex flex-col items-center gap-2 text-center">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#22c55e] shadow-sm">
              <span className="text-lg font-bold text-white">{APP_INITIALS}</span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-muted-foreground" />
              <h1 className="text-2xl font-bold tracking-tight">Two-factor verification</h1>
            </div>
            <p className="text-sm text-muted-foreground">
              {useRecoveryCode
                ? 'Enter one of your recovery codes'
                : 'Enter the 6-digit code from your authenticator app'}
            </p>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {useRecoveryCode ? 'Recovery code' : 'Authenticator code'}
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder={useRecoveryCode ? 'XXXXX-XXXXX-XXXXX' : '000000'}
                        autoComplete="one-time-code"
                        autoFocus
                        inputMode={useRecoveryCode ? undefined : 'numeric'}
                        maxLength={useRecoveryCode ? undefined : 6}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting || !challengeToken}
              >
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isSubmitting ? 'Verifying…' : 'Verify'}
              </Button>
            </form>
          </Form>

          <div className="flex flex-col gap-2 text-center text-sm">
            <button
              type="button"
              onClick={toggleMode}
              className="text-muted-foreground transition-colors hover:text-foreground"
            >
              {useRecoveryCode
                ? 'Use authenticator app instead'
                : "Can't access your app? Use a recovery code"}
            </button>
            <Link
              to="/login"
              className="flex items-center justify-center gap-1 text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
